import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { Button, Col, Input, InputRef, Row } from 'antd';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Search } from 'lucide-react';
import { TbChevronDown, TbComet } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import {
    AnimatedCollapsibleContent,
    BaseSkeleton,
    EmptySection,
    SIDER_COLLAPSE_ANIMATION_DURATION,
    SiderHoverActionSlot,
    SiderIconLabel,
    SiderTooltip,
} from '@/shared';
import {
    useDashboardStore,
    useFolderStore,
    useInvestmentStore,
    useListStore,
    useMemoStore,
    usePipelineStore,
} from '@/stores';

import FavoritesCollapsibleSection from './FavoritesCollapsibleSection';

interface WorkspaceFavoritesProps {
    bordered?: boolean;
    collapsed?: boolean;
    groupCollapsed?: Record<string, boolean | undefined>;
    onCollapsedChange?: (collapsed: boolean) => void;
    onGroupCollapsedChange?: (group: string, collapsed: boolean) => void;
}

interface FavoritableResourceLabel {
    editedName?: string;
    label?: string;
    name?: string;
    title?: string;
}

interface EmptyFavoritesProps {
    iconColor: string;
}

const EmptyFavorites: FC<EmptyFavoritesProps> = ({ iconColor }) => (
    <div style={{ width: '80%', margin: '1rem auto' }}>
        <EmptySection
            height='10rem'
            icon={<TbComet size='75%' style={{ color: iconColor }} />}
            label='No Favorites'
            description='Favorite workspace resources to have them appear here for ease of access.'
        />
    </div>
);

interface AnimatedFavoriteGroupProps {
    children: ReactNode;
    reduceMotion: boolean;
    section: string;
    visible: boolean;
}

const FAVORITES_PRESENCE_VARIANTS = {
    initial: { opacity: 0, y: -4 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -4 },
};

const AnimatedFavoriteGroup: FC<AnimatedFavoriteGroupProps> = ({ children, reduceMotion, section, visible }) => (
    <AnimatePresence initial={false}>
        {visible ? (
            <motion.div
                key={section}
                initial={reduceMotion ? false : 'initial'}
                animate='animate'
                exit={reduceMotion ? undefined : 'exit'}
                variants={FAVORITES_PRESENCE_VARIANTS}
                transition={{
                    duration: reduceMotion ? 0 : SIDER_COLLAPSE_ANIMATION_DURATION / 1000,
                    ease: 'easeInOut',
                }}
            >
                {children}
            </motion.div>
        ) : null}
    </AnimatePresence>
);

const WorkspaceFavorites: FC<WorkspaceFavoritesProps> = ({
    bordered = true,
    collapsed,
    groupCollapsed,
    onCollapsedChange,
    onGroupCollapsedChange,
}): JSX.Element => {
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setTimeout(() => {
            setLoading(false);
        }, 750);
    }, []);

    const { token } = useAntDesignTheme();
    const reduceMotion = Boolean(useReducedMotion());

    const [sectionHovered, setSectionHovered] = useState(false);

    const [uncontrolledCollapsed, setUncontrolledCollapsed] = useState<boolean>(false);
    const favoritesHidden = collapsed ?? uncontrolledCollapsed;
    const [favoritesInputValue, setFavoritesInputValue] = useState('');
    const [favoritesInputVisible, setFavoritesInputVisible] = useState(false);
    const forceFavoriteGroupsExpanded = Boolean(favoritesInputValue.trim());
    const setFavoritesHidden = (nextCollapsed: boolean) => {
        if (onCollapsedChange) {
            onCollapsedChange(nextCollapsed);

            return;
        }

        setUncontrolledCollapsed(nextCollapsed);
    };

    const normalize = (v?: string) => v?.toLowerCase().trim() ?? '';

    const matchesSearch = (item: FavoritableResourceLabel, query: string) => {
        if (!query) {
            return true;
        }

        const q = normalize(query);

        return (
            normalize(item?.editedName).includes(q) ||
            normalize(item?.name).includes(q) ||
            normalize(item?.title).includes(q) ||
            normalize(item?.label).includes(q)
        );
    };

    const { folders, fetchFolders } = useFolderStore();
    const { investments, fetchInvestments } = useInvestmentStore();
    const { memos, fetchMemos } = useMemoStore();
    const { pipelines, fetchPipelines } = usePipelineStore();
    // Relations
    const { dashboards, fetchDashboards } = useDashboardStore();
    const { lists, fetchLists } = useListStore();
    const searchQuery = favoritesInputValue;

    const favoritedFolders = folders?.filter((f) => f?.favorited)?.filter((f) => matchesSearch(f, searchQuery));

    const favoritedInvestments = investments
        ?.filter((f) => f?.isFavorite)
        ?.filter((f) => matchesSearch(f, searchQuery));

    const favoritedMemos = memos?.filter((f) => f?.favorited)?.filter((f) => matchesSearch(f, searchQuery));

    const favoritedPipelines = pipelines?.filter((f) => f?.isFavorite)?.filter((f) => matchesSearch(f, searchQuery));

    // Relations
    const favoritedDashboards = dashboards?.filter((f) => f?.favorited)?.filter((f) => matchesSearch(f, searchQuery));

    const favoritedLists = lists?.filter((f) => f?.favorited)?.filter((f) => matchesSearch(f, searchQuery));
    // !IMPORTANT: This will eventually be standardized to a single API call we'll make to /api/favorites as an alias
    useEffect(() => {
        Promise.all([
            fetchFolders(),
            fetchInvestments(),
            fetchMemos(),
            fetchPipelines(),
            // Relations
            fetchDashboards(),
            fetchLists(),
        ]);
    }, [fetchDashboards, fetchFolders, fetchInvestments, fetchLists, fetchMemos, fetchPipelines]);

    const showEmpty =
        !favoritedFolders?.length &&
        !favoritedInvestments?.length &&
        !favoritedMemos?.length &&
        !favoritedPipelines?.length &&
        !favoritedDashboards?.length &&
        !favoritedLists?.length;

    const inputRef = useRef<InputRef>(null);

    useEffect(() => {
        if (favoritesInputVisible) {
            setTimeout(() => inputRef.current?.focus(), 0);
        }
    }, [favoritesInputVisible]);

    return (
        <>
            <div
                style={{
                    borderTop: bordered ? `1px solid ${token.colorBorder}` : undefined,
                    padding: '0.5rem',
                }}
            >
                <Row
                    gutter={[12, 0]}
                    align={'middle'}
                    onMouseEnter={() => setSectionHovered(true)}
                    onMouseLeave={() => setSectionHovered(false)}
                >
                    <Col flex='none'>
                        <Button size='small' block type='text' onClick={() => setFavoritesHidden(!favoritesHidden)}>
                            <TbChevronDown
                                style={{
                                    verticalAlign: '-0.125rem',
                                    color: token.colorTextTertiary,
                                    transform: favoritesHidden ? 'rotate(-90deg)' : undefined,
                                    transition: 'transform 250ms ease',
                                }}
                            />
                        </Button>
                    </Col>
                    <Col flex='none'>
                        <small style={{ color: token.colorTextTertiary }}>
                            <SiderIconLabel
                                gap='0.25rem'
                                icon={<TbComet size='0.875rem' />}
                                label='Favorites'
                                style={{ transform: 'translateY(-1px)' }}
                            />
                        </small>
                    </Col>
                    <Col flex='auto' />
                    <SiderHoverActionSlot visible={sectionHovered}>
                        <SiderTooltip title='Search Favorites'>
                            <Button
                                className='left-sider-hover-action'
                                size='small'
                                block
                                type='text'
                                onClick={() => {
                                    setFavoritesInputVisible((b: boolean) => !b);
                                    setFavoritesInputValue('');
                                }}
                            >
                                <Search
                                    size='0.825rem'
                                    style={{
                                        verticalAlign: '-0.1rem',
                                        color: token.colorTextTertiary,
                                    }}
                                />
                            </Button>
                        </SiderTooltip>
                    </SiderHoverActionSlot>
                </Row>
                {favoritesInputVisible && (
                    <Input
                        ref={inputRef}
                        size='small'
                        prefix={
                            <Search
                                size='0.75rem'
                                style={{
                                    color: token.colorTextQuaternary,
                                    marginRight: '0.125rem',
                                }}
                            />
                        }
                        placeholder='Search Favorites...'
                        style={{
                            marginTop: '0.75rem',
                            padding: '0 0.25rem',
                            display: favoritesInputVisible ? undefined : 'none',
                        }}
                        value={favoritesInputValue}
                        onChange={(e) => setFavoritesInputValue(e.target.value)}
                        onBlur={() => {
                            setFavoritesInputVisible(false);
                        }}
                    />
                )}
                <AnimatedCollapsibleContent collapsed={favoritesHidden}>
                    <Row wrap={false} gutter={[12, 0]}>
                        <Col flex='none' style={{ display: 'flex' }}>
                            <div
                                aria-hidden='true'
                                data-favorites-section-guide='true'
                                style={{
                                    alignSelf: 'stretch',
                                    background: token.colorBorder,
                                    borderRadius: token.borderRadius,
                                    flex: '0 0 1.5px',
                                    margin: '0.25rem 0',
                                    marginLeft: '0.675rem',
                                    minHeight: '1rem',
                                    width: '1.5px',
                                }}
                            />
                        </Col>
                        <Col flex='auto'>
                            <AnimatePresence initial={false} mode='wait'>
                                {!loading && !showEmpty ? (
                                    <motion.div
                                        key='favorite-groups'
                                        initial={reduceMotion ? false : 'initial'}
                                        animate='animate'
                                        exit={reduceMotion ? undefined : 'exit'}
                                        variants={FAVORITES_PRESENCE_VARIANTS}
                                        transition={{
                                            duration: reduceMotion ? 0 : SIDER_COLLAPSE_ANIMATION_DURATION / 1000,
                                            ease: 'easeInOut',
                                        }}
                                    >
                                        <AnimatedFavoriteGroup
                                            reduceMotion={reduceMotion}
                                            section='folders'
                                            visible={Boolean(favoritedFolders?.length)}
                                        >
                                            <FavoritesCollapsibleSection
                                                collapsed={groupCollapsed?.folders}
                                                data={favoritedFolders}
                                                forceExpanded={forceFavoriteGroupsExpanded}
                                                onCollapsedChange={(nextCollapsed) =>
                                                    onGroupCollapsedChange?.('folders', nextCollapsed)
                                                }
                                                section={'folders'}
                                            />
                                        </AnimatedFavoriteGroup>
                                        <AnimatedFavoriteGroup
                                            reduceMotion={reduceMotion}
                                            section='investments'
                                            visible={Boolean(favoritedInvestments?.length)}
                                        >
                                            <FavoritesCollapsibleSection
                                                collapsed={groupCollapsed?.investments}
                                                data={favoritedInvestments}
                                                forceExpanded={forceFavoriteGroupsExpanded}
                                                onCollapsedChange={(nextCollapsed) =>
                                                    onGroupCollapsedChange?.('investments', nextCollapsed)
                                                }
                                                section={'investments'}
                                            />
                                        </AnimatedFavoriteGroup>
                                        <AnimatedFavoriteGroup
                                            reduceMotion={reduceMotion}
                                            section='memos'
                                            visible={Boolean(favoritedMemos?.length)}
                                        >
                                            <FavoritesCollapsibleSection
                                                collapsed={groupCollapsed?.memos}
                                                data={favoritedMemos}
                                                forceExpanded={forceFavoriteGroupsExpanded}
                                                onCollapsedChange={(nextCollapsed) =>
                                                    onGroupCollapsedChange?.('memos', nextCollapsed)
                                                }
                                                section={'memos'}
                                            />
                                        </AnimatedFavoriteGroup>
                                        <AnimatedFavoriteGroup
                                            reduceMotion={reduceMotion}
                                            section='pipelines'
                                            visible={Boolean(favoritedPipelines?.length)}
                                        >
                                            <FavoritesCollapsibleSection
                                                collapsed={groupCollapsed?.pipelines}
                                                data={favoritedPipelines}
                                                forceExpanded={forceFavoriteGroupsExpanded}
                                                onCollapsedChange={(nextCollapsed) =>
                                                    onGroupCollapsedChange?.('pipelines', nextCollapsed)
                                                }
                                                section={'pipelines'}
                                            />
                                        </AnimatedFavoriteGroup>
                                        {/* Relations */}
                                        <AnimatedFavoriteGroup
                                            reduceMotion={reduceMotion}
                                            section='dashboards'
                                            visible={Boolean(favoritedDashboards?.length)}
                                        >
                                            <FavoritesCollapsibleSection
                                                collapsed={groupCollapsed?.dashboards}
                                                data={favoritedDashboards}
                                                forceExpanded={forceFavoriteGroupsExpanded}
                                                onCollapsedChange={(nextCollapsed) =>
                                                    onGroupCollapsedChange?.('dashboards', nextCollapsed)
                                                }
                                                section={'dashboards'}
                                            />
                                        </AnimatedFavoriteGroup>
                                        <AnimatedFavoriteGroup
                                            reduceMotion={reduceMotion}
                                            section='lists'
                                            visible={Boolean(favoritedLists?.length)}
                                        >
                                            <FavoritesCollapsibleSection
                                                collapsed={groupCollapsed?.lists}
                                                data={favoritedLists}
                                                forceExpanded={forceFavoriteGroupsExpanded}
                                                onCollapsedChange={(nextCollapsed) =>
                                                    onGroupCollapsedChange?.('lists', nextCollapsed)
                                                }
                                                section={'lists'}
                                            />
                                        </AnimatedFavoriteGroup>
                                    </motion.div>
                                ) : loading ? (
                                    <motion.div
                                        key='favorites-loading'
                                        initial={reduceMotion ? false : 'initial'}
                                        animate='animate'
                                        exit={reduceMotion ? undefined : 'exit'}
                                        variants={FAVORITES_PRESENCE_VARIANTS}
                                        transition={{
                                            duration: reduceMotion ? 0 : SIDER_COLLAPSE_ANIMATION_DURATION / 1000,
                                            ease: 'easeInOut',
                                        }}
                                    >
                                        <BaseSkeleton
                                            title={false}
                                            paragraph={{ rows: 12 }}
                                            style={{ marginTop: '0.75rem', color: token.colorTextTertiary }}
                                        />
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key='favorites-empty'
                                        initial={reduceMotion ? false : 'initial'}
                                        animate='animate'
                                        exit={reduceMotion ? undefined : 'exit'}
                                        variants={FAVORITES_PRESENCE_VARIANTS}
                                        transition={{
                                            duration: reduceMotion ? 0 : SIDER_COLLAPSE_ANIMATION_DURATION / 1000,
                                            ease: 'easeInOut',
                                        }}
                                    >
                                        <EmptyFavorites iconColor={token.colorTextTertiary} />
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </Col>
                    </Row>
                </AnimatedCollapsibleContent>
            </div>
        </>
    );
};

export default WorkspaceFavorites;
